import { useEffect, useMemo, useState } from 'react';
import { withNamespaces } from 'react-i18next';

import TableFilter from 'src/components/TableFilter';
import {
    ThirdPartyDocumentCategories,
    ThirdPartyDocumentConfidentialityLevels,
    ThirdPartyDocumentTypes,
    ThirdPartyDocumentUploadedByTypes,
} from 'src/modules/3rd-party-management/constants';

const EMPTY_FILTERS = {
    type: undefined,
    category: undefined,
    confidentialityLevel: undefined,
    uploadedByType: undefined,
    title: undefined,
};

const DocumentFilters = ({
    t,
    onChange,
    confidentialityLevels = ThirdPartyDocumentConfidentialityLevels,
}) => {
    const [filters, setFilters] = useState(EMPTY_FILTERS);

    const tabs = useMemo(() => {
        return [
            {
                type: 'radio_list',
                name: 'type',
                title: 'Type',
                icon: <i className="ri-exchange-line" />,
                isLoading: false,
                defaultValue: filters.type,
                data: ThirdPartyDocumentTypes.map((item) => ({
                    id: item.value,
                    ...item,
                })),
                searchInListOption: {
                    enabled: true,
                    remote: false,
                },
                formatter: (item) => t(item.title),
            },
            {
                type: 'radio_list',
                name: 'category',
                title: 'Category',
                icon: <i className="ri-price-tag-3-line" />,
                isLoading: false,
                defaultValue: filters.category,
                data: ThirdPartyDocumentCategories.map((item) => ({
                    id: item.value,
                    ...item,
                })),
                searchInListOption: {
                    enabled: true,
                    remote: false,
                },
                formatter: (item) => t(item.title),
            },
            {
                type: 'radio_list',
                name: 'confidentialityLevel',
                title: 'Confidentiality Level',
                icon: <i className="ri-shield-line" />,
                isLoading: false,
                defaultValue: filters.confidentialityLevel,
                data: confidentialityLevels.map((item) => ({
                    id: item.value,
                    ...item,
                })),
                searchInListOption: {
                    enabled: true,
                    remote: false,
                },
                formatter: (item) => t(item.title),
            },
            {
                type: 'radio_list',
                name: 'uploadedByType',
                title: 'Uploaded By Type',
                icon: <i className="ri-user-line" />,
                isLoading: false,
                defaultValue: filters.uploadedByType,
                data: ThirdPartyDocumentUploadedByTypes.map((item) => ({
                    id: item.value,
                    ...item,
                })),
                searchInListOption: {
                    enabled: true,
                    remote: false,
                },
                formatter: (item) => t(item.title),
            },
            {
                type: 'search_textbox',
                name: 'title',
                title: 'Search',
                icon: <i className="ri-search-line" />,
                defaultValue: filters.title,
                options: {
                    placeholder: t('Search by title'),
                    typingDelay: 750,
                },
                displayIndependently: true,
                onFilterChange: (title) => {
                    setFilters((currentFilters) => ({
                        ...currentFilters,
                        title,
                    }));
                },
            },
        ];
    }, [confidentialityLevels, filters, t]);

    const handleApply = (nextFilters, independentTabsData) => {
        if (!nextFilters) {
            setFilters({
                ...EMPTY_FILTERS,
                title: independentTabsData?.title,
            });

            return;
        }

        setFilters((currentFilters) => ({
            ...currentFilters,
            type: nextFilters.type && nextFilters.type !== 'all'
                ? nextFilters.type
                : undefined,
            category: nextFilters.category && nextFilters.category !== 'all'
                ? nextFilters.category
                : undefined,
            confidentialityLevel:
                nextFilters.confidentialityLevel
                && nextFilters.confidentialityLevel !== 'all'
                    ? nextFilters.confidentialityLevel
                    : undefined,
            uploadedByType:
                nextFilters.uploadedByType
                && nextFilters.uploadedByType !== 'all'
                    ? nextFilters.uploadedByType
                    : undefined,
        }));
    };

    useEffect(() => {
        onChange?.(filters);
    }, [filters, onChange]);

    return (
        <TableFilter
            className="me-2"
            defaultActiveTab="type"
            tabs={tabs}
            onOkButtonClicked={handleApply}
        />
    );
};

export default withNamespaces()(DocumentFilters);
